import { afterNextRender, afterRenderEffect, Component, computed, DestroyRef, ElementRef, inject, input, model, viewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { drawStroke, prepareCanvas, type SignatureStroke } from "./signature-canvas";

export type { SignatureStroke } from "./signature-canvas";

/**
 * Native canvas "sign here" pad for capturing a signature. Pointer Events cover mouse, finger and stylus in
 * one path, and `touch-action: none` keeps a finger stroke from scrolling the page on phones/tablets.
 * Strokes are kept in this pad's CSS pixels; show them elsewhere with SignatureView, which fits them to its box.
 * Size it from the parent (default 160px tall, full width).
 */
@Component({
  selector: "lib-signature-pad",
  imports: [MatButtonModule],
  template: `
    <canvas
      #canvas
      role="img"
      [attr.aria-label]="label() + ' signature pad' + ($signed() ? ', signed' : ', not signed')"
      (pointerdown)="start($event)"
      (pointermove)="move($event)"
      (pointerup)="end($event)"
      (pointercancel)="end($event)"
    ></canvas>
    <span class="baseline" aria-hidden="true"></span>
    @if (!$signed()) {
      <span class="placeholder" aria-hidden="true">Sign here</span>
    } @else {
      <button matButton type="button" class="clear" [attr.aria-label]="'Clear ' + label() + ' signature'" (click)="clear()">
        Clear
      </button>
    }
  `,
  styles: `
    :host {
      position: relative;
      display: block;
      height: 160px;
      background: #fff;
    }
    canvas {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
      touch-action: none;
      cursor: crosshair;
    }
    .baseline {
      position: absolute;
      left: 16px;
      right: 16px;
      bottom: 28px;
      border-bottom: 1px solid #9e9e9e;
      pointer-events: none;
    }
    .placeholder {
      position: absolute;
      left: 16px;
      bottom: 6px;
      font-size: 12px;
      color: #616161;
      pointer-events: none;
    }
    .clear {
      position: absolute;
      right: 4px;
      top: 4px;
    }
  `,
})
export class SignaturePad {
  readonly label = input.required<string>();
  /** Finished strokes, owned by the caller. */
  readonly strokes = model<SignatureStroke[]>([]);

  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>("canvas");
  #active: SignatureStroke | null = null;

  readonly $signed = computed(() => this.strokes().length > 0);

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const observer = new ResizeObserver(() => this.#redraw());
      observer.observe(this.canvas().nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
    });
    // Clear (or the caller) replaced the strokes; repaint from the source of truth.
    afterRenderEffect({ write: () => this.#redraw(this.strokes()) });
  }

  clear() {
    this.strokes.set([]);
  }

  start(event: PointerEvent) {
    if (event.button !== 0) return;
    event.preventDefault();
    this.canvas().nativeElement.setPointerCapture(event.pointerId);
    this.#active = [this.#point(event)];
    this.#drawActive();
  }

  move(event: PointerEvent) {
    if (!this.#active) return;
    event.preventDefault();
    const coalesced = event.getCoalescedEvents?.() ?? [];
    for (const e of coalesced.length ? coalesced : [event]) {
      this.#active.push(this.#point(e));
    }
    this.#drawActive();
  }

  end(event: PointerEvent) {
    const stroke = this.#active;
    if (!stroke) return;
    this.canvas().nativeElement.releasePointerCapture(event.pointerId);
    this.#active = null;
    this.strokes.update((strokes) => [...strokes, stroke]);
  }

  // Divides out any CSS transform on an ancestor, so strokes stay in the canvas's own CSS pixels.
  #point(event: PointerEvent) {
    const canvas = this.canvas().nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width ? canvas.clientWidth / rect.width : 1;
    const scaleY = rect.height ? canvas.clientHeight / rect.height : 1;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  }

  #drawActive() {
    const ctx = this.canvas().nativeElement.getContext("2d");
    if (ctx && this.#active) drawStroke(ctx, this.#active);
  }

  #redraw(strokes = this.strokes()) {
    const ctx = prepareCanvas(this.canvas().nativeElement);
    if (!ctx) return;
    for (const stroke of strokes) drawStroke(ctx, stroke);
  }
}
